import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { Globe, ShoppingBag, Heart, Video, Calendar, BookOpen, Loader2 } from "lucide-react";

const LOGO = "/ashra-logo.png";

export default function PublicHub() {
  const { handle } = useParams();
  const [state, setState] = useState({ loading: true, hub: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/public/hub/${handle}`);
        if (!cancelled) setState({ loading: false, hub: r.data });
      } catch {
        if (!cancelled) setState({ loading: false, hub: null });
      }
    })();
    return () => { cancelled = true; };
  }, [handle]);

  if (state.loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FAFAFA] text-meta">
        <div className="inline-flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      </div>
    );
  }
  if (!state.hub) return <Navigate to="/" replace />;

  const hub = state.hub;
  const theme = hub.theme_color || null;
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink" data-testid="public-hub-page">
      <header className="border-b border-line bg-white/90 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <img src={LOGO} alt="Ashra" className="h-6 opacity-70" />
          <span className="text-[11px] text-meta">ashraai.com/{handle}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <div className={`mx-auto h-20 w-20 rounded-3xl grid place-items-center text-white text-2xl font-extrabold shadow-sm ${theme ? "" : "ashra-gradient-bg"}`}
            style={theme ? { background: `linear-gradient(135deg, ${theme}, ${theme}cc)` } : undefined}>
            {(hub.name || handle)[0]?.toUpperCase()}
          </div>
          <h1 className="mt-5 font-display text-3xl font-extrabold text-ink">{hub.name || `@${handle}`}</h1>
          {hub.tagline && <p className="mt-2 text-meta">{hub.tagline}</p>}
          {hub.cta_label && hub.cta_url && (
            <a href={hub.cta_url} target="_blank" rel="noopener noreferrer" data-testid="public-hub-cta"
               className="mt-5 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
               style={{ background: theme || "#7C3AED" }}>
              {hub.cta_label}
            </a>
          )}
        </div>

        <div className="space-y-3" data-testid="public-hub-offers">
          {(hub.offers || []).length === 0 && (
            <div className="surface p-10 text-center text-meta">
              <Globe className="h-6 w-6 mx-auto text-meta-soft mb-2" />
              <p className="text-sm">This hub is being prepared. Check back soon.</p>
            </div>
          )}
          {(hub.offers || []).map((o) => {
            const Icon = { product: ShoppingBag, course: BookOpen, membership: Heart, webinar: Video, booking: Calendar }[o.type] || ShoppingBag;
            return (
              <a key={o.id} href={o.url || "#"} target="_blank" rel="noopener noreferrer"
                 className="surface p-5 flex items-center gap-4 hover:shadow-md transition" data-testid={`hub-offer-${o.id}`}>
                <div className="h-10 w-10 rounded-xl bg-ai-soft text-ai grid place-items-center"><Icon className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink truncate">{o.title}</p>
                  {o.subtitle && <p className="text-xs text-meta truncate">{o.subtitle}</p>}
                </div>
                {o.price != null && <p className="font-display text-lg font-extrabold text-ink shrink-0">${o.price}</p>}
              </a>
            );
          })}
        </div>

        <p className="text-center mt-12 text-[11px] text-meta-soft">Powered by Ashra</p>
      </main>
    </div>
  );
}
